import { AppNS } from 'app';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';
import axios, { Canceler } from 'axios';

import { API_URL } from '../../core/config';
import { parseDocumentResponse } from '../agency-documents/agency-documents-parsers';
import { ApiNS } from '../api';
import { getErrorMessagesAsArray } from '../helpers/errorHandling';
import { parseReport } from './agency-reports-parsers';

const URL = 'reports';
const TIME_URL = 'update-time';
const DOCUMENTS_URL = 'documents';
const REPORTS_URL = 'reports';
const INSPECTOR_URL = 'update-inspector';
const PIC_INSPECTOR_URL = 'update-pic-inspector';
const UPDATE_AMOUNT = 'update-amount';
const NOTES_URL = 'notes';
const QUESTIONNAIRE_URL = 'inspection-questionnaire';
const CHECKLIST_URL = 'inspection-checklist';
const EVENT_INSPECTIONS = 'event-inspections';
const PERMITS_URL = 'permits';

const CancelToken = axios.CancelToken;
let cancel: Canceler;
let historyCancel: Canceler;

export function apiGetReport(id: string): Promise<AppNS.JobEventReport> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${URL}/${id}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseReport(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSubmitReport(
  report: AppNS.JobEventReport
): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/submit.json`, {
        id: report.id,
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiAddReportDocument(reqData: AppNS.Params): Promise<FileInfo> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${DOCUMENTS_URL}.json?return_properties=true`, reqData)
      .then(({ data }) => {
        if (data.model) {
          // Added timeout because of s3 404 error when private url is invoked immediately
          setTimeout(() => {
            resolve(parseDocumentResponse(data.model));
          }, 500);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiDeleteReportDocument(id: string): Promise<FileInfo> {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${API_URL}/${DOCUMENTS_URL}.json`, { params: { id } })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetReports(
  query: string,
  dateFrom: string,
  dateTo: string,
  page: number,
  searchBy: string,
  state: string,
  agencyCompanyId?: string,
  states?: string[],
  inspectionsListing?: boolean
): Promise<ApiNS.ReportsListResponse> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${API_URL}/${REPORTS_URL}${
          inspectionsListing ? '/inspections_listing' : ''
        }.json`,
        {
          cancelToken: new CancelToken(function executor(c) {
            // An executor function receives a cancel function as a parameter
            cancel = c;
          }),
          params: {
            query,
            search_by: searchBy,
            date_from: dateFrom,
            date_to: dateTo,
            states,
            page,
            state,
            agency_company_id: agencyCompanyId,
          },
        }
      )
      .then(({ data }) => {
        if (data.results) {
          resolve(data);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetReportsCsv(
  query: string,
  dateFrom: string,
  dateTo: string,
  searchBy: string,
  state: string
): Promise<Blob> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${REPORTS_URL}.csv`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          query,
          search_by: searchBy,
          date_from: dateFrom,
          date_to: dateTo,
          states: [
            'paid',
            'finalized_report',
            'payment_pending',
            'payment_failed',
          ],
          state,
        },
        headers: {
          Accept: 'text/csv',
        },
      })
      .then(({ data }) => {
        if (data) {
          resolve(new Blob([data], { type: 'text/csv' }));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSaveReportInspector(
  id: string,
  properties: { inspectorId?: string; picInspectorId?: string }
): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    const req = {
      id,
      properties,
    };
    const endpoint = properties.picInspectorId
      ? PIC_INSPECTOR_URL
      : INSPECTOR_URL;
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${endpoint}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSaveReportTime(
  id: string,
  date: string,
  timeFrom: string | null,
  timeTo: string | null
): Promise<string> {
  const req = {
    id,
    properties: {
      date,
      timeFrom,
      timeTo,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${TIME_URL}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiSaveReportNotes(id: string, notes: string): Promise<string> {
  const req = {
    id,
    properties: {
      notes,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${NOTES_URL}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiSaveReportQuestionnaire(
  id: string,
  inspectionQuestionnaire: AppNS.Params
): Promise<
  Pick<AppNS.JobEventReport, 'inspectionQuestionnaire' | 'eventInspections'>
> {
  const req = {
    id,
    properties: {
      inspectionQuestionnaire,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${QUESTIONNAIRE_URL}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve({
            inspectionQuestionnaire:
              data.model.properties.inspectionQuestionnaire,
            eventInspections: data.model.properties.eventInspections,
          });
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiSaveReportChecklist(
  id: string,
  inspectionChecklist: AppNS.Params
): Promise<Pick<AppNS.JobEventReport, 'inspectionChecklist'>> {
  const req = {
    id,
    properties: {
      inspectionChecklist,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${CHECKLIST_URL}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve({
            inspectionChecklist: data.model.properties.inspectionChecklist,
          });
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiUpdateReportInspection(
  id: string,
  notes: string,
  state: string,
  failedCodes: string[],
  customFailedCodes: string
): Promise<string> {
  const req = {
    id,
    properties: {
      notes,
      state,
      failedCodes,
      customFailedCodes,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${EVENT_INSPECTIONS}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiUpdateReportDcraInspection(
  id: string,
  notes: string,
  state: string,
  failedCodes: string[],
  customFailedCodes: string,
  gsComments: string,
  recordedAmperage: string,
  electricalSupplementalType: string
): Promise<string> {
  const req = {
    id,
    properties: {
      notes,
      state,
      failedCodes,
      customFailedCodes,
      gsComments,
      recordedAmperage,
      electricalSupplementalType,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${EVENT_INSPECTIONS}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiUpdateReportTotalCost(
  id: string,
  cost: number
): Promise<AppNS.Params> {
  const req = {
    id,
    properties: {
      requested_amount: cost,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${REPORTS_URL}/${UPDATE_AMOUNT}.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiGetPermitHistory(id: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${PERMITS_URL}/${id}/inspections-history.json`)
      .then(({ data }) => {
        resolve(true);
      })
      .catch(reject);
  });
}

export function apiGetPermitHistoryFilesList(
  permitId: string,
  id: string
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${PERMITS_URL}/${permitId}/inspection-files`, {
        params: {
          accela_inspection_id: id,
        },
      })
      .then(({ data }) => {
        resolve(true);
      })
      .catch(reject);
  });
}

export function apiGetPermitHistoryFile(
  permitId: string,
  id: string
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${PERMITS_URL}/${permitId}/download-file`, {
        params: {
          accela_file_id: id,
        },
      })
      .then(({ data }) => {
        resolve(true);
      })
      .catch(reject);
  });
}
